import type { PrescriptionProduct, SelectOption } from './types'
import { getSearchKeywords } from '@/lib/drugSynonyms'

/**
 * Build a product map from API prescription products.
 */
export function buildProductMap(
  prescriptionProducts: PrescriptionProduct[] | undefined,
): Record<string, PrescriptionProduct> {
  const pMap: Record<string, PrescriptionProduct> = {}
  if (prescriptionProducts && prescriptionProducts.length > 0) {
    for (const product of prescriptionProducts) {
      pMap[product.key] = product
    }
  }
  return pMap
}

/**
 * Get medication/product options for the dropdown, optionally filtered by program.
 */
export function getProductOptions(
  productMap: Record<string, PrescriptionProduct>,
  programId?: string | null,
  programs?: any[] | null
): SelectOption[] {
  const options: SelectOption[] = []
  const seen = new Set<string>()

  // Resolve program title from programs array for matching
  // (handles duplicate clinic programs where IDs don't match between endpoints)
  let filterProgramTitle: string | null = null
  if (programId) {
    // First check if any product directly matches this program ID
    const hasDirectMatch = Object.values(productMap).some(p => p.program_id === programId)
    if (hasDirectMatch) {
      // Direct ID match works — use it
      filterProgramTitle = null
    } else if (programs) {
      // No direct match — resolve by program title
      const selectedProgram = programs.find((p: any) => p.id === programId)
      filterProgramTitle = selectedProgram?.title || null
    }
  }

  Object.values(productMap).forEach((product) => {
    if (programId) {
      // When a program is selected, only show products belonging to that program
      if (!product.program_id) return
      if (filterProgramTitle) {
        if (product.program_title !== filterProgramTitle) return
      } else {
        if (product.program_id !== programId) return
      }
    }
    const dedupeKey = `${product.medication_name}::${product.dose}`
    if (seen.has(dedupeKey)) return
    seen.add(dedupeKey)
    options.push({
      label: product.dose
        ? `${product.medication_name} ${product.dose}`
        : product.medication_name,
      value: product.key,
      searchKeywords: getSearchKeywords(product.medication_name),
    })
  })

  options.sort((a, b) => a.label.localeCompare(b.label))
  return options
}

/**
 * Get program options for the program dropdown.
 */
export function getProgramOptions(programs: any[] | undefined): SelectOption[] {
  if (!programs || !Array.isArray(programs)) return []
  return programs
    .filter((p: any) => !p.is_deleted && p.display_mode !== 'protocol_showcase')
    .map((p: any) => ({ label: p.title, value: p.id }))
}

/**
 * Get dose options for a selected medication.
 */
export function getDoseOptions(
  productMap: Record<string, PrescriptionProduct>,
  medicationKey: string
): SelectOption[] {
  const product = productMap[medicationKey]
  if (product?.dose) {
    return [{ label: product.dose, value: product.dose }]
  }
  return []
}

/**
 * Check if dose should be disabled for a selected medication.
 */
export function isDoseDisabled(
  productMap: Record<string, PrescriptionProduct>,
  medicationKey: string
): boolean {
  const product = productMap[medicationKey]
  return !!product?.dose
}

/**
 * Get sig (instruction) options for a selected medication.
 */
export function getSigOptions(
  productMap: Record<string, PrescriptionProduct>,
  medicationKey: string
): SelectOption[] {
  const product = productMap[medicationKey]
  const sigs = product?.default_sigs || []
  return sigs.map((s) => ({ label: String(s), value: String(s) }))
}

/**
 * Get quantity options for a selected medication.
 */
export function getQuantityOptions(
  productMap: Record<string, PrescriptionProduct>,
  medicationKey: string
): SelectOption[] {
  const product = productMap[medicationKey]
  const qtys = product?.qty_options || []
  return qtys.map((q) => ({ label: String(q), value: String(q) }))
}

/**
 * Resolve form values for submission — maps product keys back to display names, product_ids, etc.
 */
export function resolveSubmitValues(
  medications: any[],
  productMap: Record<string, PrescriptionProduct>
): any[] {
  return medications.map((med) => {
    const product = productMap[med.medication]
    if (product) {
      return {
        ...med,
        medication: product.medication_name,
        dose: product.dose || med.dose,
        unit: med.unit || product.unit_of_measure || '',
        product_id: product.product_id,
        template_id: product.template_id,
        program_id: product.program_id || med.program_id || null,
        price: product.price,
        base_price: product.base_price,
      }
    }
    // Custom entry — no resolution needed
    return { ...med, product_id: null, template_id: null }
  })
}
